import React, { useState, useEffect } from 'react';
import {
  Layout, Menu, Card, Table, Button, Modal, Form, Input, InputNumber,
  Tabs, Upload, Space, Typography, Statistic, Row, Col, message,
  Tag, Divider, Alert, Select, Progress
} from 'antd';
import {
  SettingOutlined, UserOutlined, BarChartOutlined, UploadOutlined,
  DownloadOutlined, ExclamationCircleOutlined, DollarOutlined,
  CalculatorOutlined, InfoCircleOutlined, GlobalOutlined, DeleteOutlined
} from '@ant-design/icons';
import DatabaseManager from './utils/DatabaseManager';
import CommissionCalculator from './utils/CommissionCalculator';
import * as XLSX from 'xlsx';
import bcrypt from 'bcryptjs';
import InteractiveSimulator from './components/InteractiveSimulator';



const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const AdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('parameters');
  const [activeBudgetTab, setActiveBudgetTab] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [tiposPresupuesto, setTiposPresupuesto] = useState([]);
  const [bandasMensuales, setBandasMensuales] = useState([]);
  const [bandasTrimestrales, setBandasTrimestrales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('parameter');
  const [form] = Form.useForm();
  const [ejecutivos, setEjecutivos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [gruposCiudades, setGruposCiudades] = useState([]);
  const [medios, setMedios] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [comisionesResumen, setComisionesResumen] = useState({
    totalMensual: 0,
    totalTrimestral: 0,
    totalEjecutivos: 0,
    promedioMensual: 0,
    promedioTrimestral: 0
  });
  const [datosCargados, setDatosCargados] = useState({ presupuestos: [], ventas: [] });
  const [filtroEjecutivo, setFiltroEjecutivo] = useState(null);
  const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());
  const [filtroTrimestre, setFiltroTrimestre] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load parameters
      const params = await DatabaseManager.Parametro.findAll();
      setParameters(params);

      // Load bands
      const mensuales = await DatabaseManager.BandaMensual.findAll({
        where: { activo: true },
        order: [['desde', 'ASC']]
      });
      setBandasMensuales(mensuales);

      const trimestrales = await DatabaseManager.BandaTrimestral.findAll({
        where: { activo: true },
        order: [['desde', 'ASC']]
      });
      setBandasTrimestrales(trimestrales);

      // Load executives
      const ejecs = await DatabaseManager.Ejecutivo.findAll({
        where: { activo: true },
        include: [{ model: DatabaseManager.Ciudad }],
        order: [['nombre', 'ASC']]
      });
      setEjecutivos(ejecs);

      // Load cities and groups for selects
      const cities = await DatabaseManager.Ciudad.findAll({ include: [{ model: DatabaseManager.GrupoCiudad }] });
      setCiudades(cities);
      const groups = await DatabaseManager.GrupoCiudad.findAll();
      setGruposCiudades(groups);
      const mds = await DatabaseManager.Medio.findAll();
      setMedios(mds);

      const budgetTypes = await DatabaseManager.TipoPresupuesto.findAll();
      setTiposPresupuesto(budgetTypes);
      if (budgetTypes.length > 0 && !activeBudgetTab) {
        setActiveBudgetTab(budgetTypes[0].id.toString());
      }

      // Load commission summary
      loadCommissionSummary();
      loadDatosCargados();

    } catch (error) {
      message.error('Error al cargar datos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDatosCargados = async () => {
    try {
      const [pptos, sales] = await Promise.all([
        DatabaseManager.Presupuesto.findAll({
          where: { activo: true },
          include: [
            { model: DatabaseManager.Ejecutivo },
            { model: DatabaseManager.Medio },
            { model: DatabaseManager.TipoPresupuesto }
          ],
          order: [['anio', 'DESC'], ['mes', 'DESC']]
        }),
        DatabaseManager.Venta.findAll({
          where: { activo: true },
          include: [
            { model: DatabaseManager.Ejecutivo },
            { model: DatabaseManager.Medio },
            { model: DatabaseManager.TipoPresupuesto }
          ],
          order: [['anio', 'DESC'], ['mes', 'DESC']]
        })
      ]);
      setDatosCargados({ presupuestos: pptos, ventas: sales });
    } catch (e) {
      console.error("Error cargando datos:", e);
    }
  };

  const loadCommissionSummary = async () => {
    try {
      const { data: calculos } = await DatabaseManager.CalculoComision.findAll({
        where: { anio: filtroAnio }
      });

      if (calculos) {
        // Filtrar por mes o trimestre si están seleccionados
        const filtered = calculos.filter(c => {
          const matchMes = !filtroMes || c.mes === filtroMes;
          const matchTrimestre = !filtroTrimestre || Math.ceil(c.mes / 3) === filtroTrimestre;
          const matchEjecutivo = !filtroEjecutivo || c.ejecutivoId === filtroEjecutivo;
          return matchMes && matchTrimestre && matchEjecutivo;
        });

        const totalMensual = filtered.reduce((sum, c) => sum + (c.comisionMensual || 0), 0);
        const totalTrimestral = filtered.reduce((sum, c) => sum + (c.comisionTrimestral || 0), 0);
        const totalEjecutivos = new Set(filtered.map(c => c.ejecutivoId)).size;

        setComisionesResumen({
          totalMensual,
          totalTrimestral,
          totalEjecutivos
        });
      }
    } catch (error) {
      console.error("Error loading summary:", error);
    }
  };

  const handleEdit = (record, type) => {
    setModalType(type);
    setEditingRecord(record);
    setModalVisible(true);
    form.setFieldsValue(record);
  };

  const handleAdd = (type, initialValues = {}) => {
    setModalType(type);
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (modalType === 'parameter') {
        if (editingRecord) {
          await DatabaseManager.Parametro.update(values, { where: { id: editingRecord.id } });
          message.success('Parámetro actualizado');
        } else {
          await DatabaseManager.Parametro.create(values);
          message.success('Parámetro creado');
        }
      } else if (modalType === 'bandaMensual' || modalType === 'bandaTrimestral') {
        const Model = modalType === 'bandaMensual' ? DatabaseManager.BandaMensual : DatabaseManager.BandaTrimestral;
        if (editingRecord) {
          await Model.update(values, { where: { id: editingRecord.id } });
          message.success('Banda actualizada');
        } else {
          await Model.create({ ...values, activo: true });
          message.success('Banda creada');
        }
      } else if (modalType === 'executive') {
        const { password, ...otherValues } = values;
        const executiveData = { ...otherValues, activo: true };

        // Solo hashear y guardar la contraseña si el usuario escribió algo
        if (password && password.trim() !== '') {
          executiveData.password = await bcrypt.hash(password, 10);
        }

        if (editingRecord) {
          await DatabaseManager.Ejecutivo.update(executiveData, { where: { id: editingRecord.id } });
          message.success('Ejecutivo actualizado');
        } else {
          // Para nuevos ejecutivos la contraseña sí es obligatoria
          await DatabaseManager.Ejecutivo.create(executiveData);
          message.success('Ejecutivo creado');
        }
      } else if (modalType === 'presupuesto') {
        if (editingRecord) {
          await DatabaseManager.Presupuesto.update(values, { where: { id: editingRecord.id } });
          message.success('Presupuesto actualizado');
        }
      } else if (modalType === 'venta') {
        if (editingRecord) {
          await DatabaseManager.Venta.update(values, { where: { id: editingRecord.id } });
          message.success('Venta actualizada');
        }
      } else if (modalType === 'medio') {
        if (editingRecord) {
          await DatabaseManager.Medio.update(values, { where: { id: editingRecord.id } });
          message.success('Medio actualizado');
        } else {
          await DatabaseManager.Medio.create(values);
          message.success('Medio creado');
        }
      } else if (modalType === 'tipoPresupuesto') {
        if (editingRecord) {
          await DatabaseManager.TipoPresupuesto.update(values, { where: { id: editingRecord.id } });
          message.success('Tipo de presupuesto actualizado');
        } else {
          await DatabaseManager.TipoPresupuesto.create(values);
          message.success('Tipo de presupuesto creado');
        }
      } else if (modalType === 'grupoCiudad') {
        if (editingRecord) {
          await DatabaseManager.GrupoCiudad.update(values, { where: { id: editingRecord.id } });
          message.success('Grupo de ciudades actualizado');
        } else {
          await DatabaseManager.GrupoCiudad.create(values);
          message.success('Grupo de ciudades creado');
        }
      } else if (modalType === 'ciudad') {
        if (editingRecord) {
          await DatabaseManager.Ciudad.update(values, { where: { id: editingRecord.id } });
          message.success('Ciudad actualizada');
        } else {
          await DatabaseManager.Ciudad.create(values);
          message.success('Ciudad creada');
        }
      }

      setModalVisible(false);
      setEditingRecord(null);
      form.resetFields();
      loadData();
    } catch (error) {
      message.error('Error al guardar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record, type) => {
    Modal.confirm({
      title: '¿Estás seguro de eliminar este registro?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          setLoading(true);
          if (type === 'parameter') {
            await DatabaseManager.Parametro.destroy({ where: { id: record.id } });
          } else if (type === 'bandaMensual') {
            await DatabaseManager.BandaMensual.destroy({ where: { id: record.id } });
          } else if (type === 'bandaTrimestral') {
            await DatabaseManager.BandaTrimestral.destroy({ where: { id: record.id } });
          } else if (type === 'executive') {
            await DatabaseManager.Ejecutivo.destroy({ where: { id: record.id } });
          } else if (type === 'presupuesto') {
            await DatabaseManager.Presupuesto.destroy({ where: { id: record.id } });
          } else if (type === 'venta') {
            await DatabaseManager.Venta.destroy({ where: { id: record.id } });
          } else if (type === 'medio') {
            await DatabaseManager.Medio.destroy({ where: { id: record.id } });
          } else if (type === 'tipoPresupuesto') {
            await DatabaseManager.TipoPresupuesto.destroy({ where: { id: record.id } });
          } else if (type === 'grupoCiudad') {
            await DatabaseManager.GrupoCiudad.destroy({ where: { id: record.id } });
          } else if (type === 'ciudad') {
            await DatabaseManager.Ciudad.destroy({ where: { id: record.id } });
          }
          message.success('Registro eliminado exitosamente');
          loadData();
        } catch (error) {
          console.error('Error al eliminar:', error);
          message.error('No se pudo eliminar el registro: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleVaciarTodo = () => {
    Modal.confirm({
      title: '¿Estás seguro de vaciar toda la base de datos?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción eliminará todos los presupuestos y ventas registradas de forma permanente. No se puede deshacer.',
      okText: 'Sí, borrar todo',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setLoading(true);
          // Supabase requiere un filtro para borrar. Usamos activo: true para borrar todos los cargados.
          await DatabaseManager.Presupuesto.destroy({ where: { activo: true } });
          await DatabaseManager.Venta.destroy({ where: { activo: true } });
          message.success('Base de datos vaciada con éxito');
          loadDatosCargados();
        } catch (error) {
          message.error('Error al vaciar datos: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleImportEjecutivos = async (file) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      let successCount = 0;
      let errorCount = 0;

      for (const row of jsonData) {
        // Mapeo flexible de columnas
        const nombre = row.nombre || row.Nombre || row.nombreCompleto;
        const email = row.email || row.Email || row.correo;
        const cedula = row.cedula || row.Cedula || row.identificacion || row.CC || row.cc || '';
        const ciudadNombre = row.ciudad || row.Ciudad || row.sede;
        const perfil = row.perfil || row.Perfil || row.perfilVenta || 'Ejecutivo';
        const role = row.rol || row.Rol || row.role || 'employee';
        const salario = parseFloat(row.salario || row.Salario || row.salarioBase || 0);
        const password = row.password || row.Password || row.contrasena || 'rcn123';

        if (!email || !nombre) {
          console.error("Fila omitida por falta de email o nombre:", row);
          errorCount++;
          continue;
        }

        // Buscar ciudad
        let ciudadId = 1; // Default
        const ciudad = ciudades.find(c => c.nombre.toLowerCase().trim() === ciudadNombre?.toLowerCase().trim());
        if (ciudad) ciudadId = ciudad.id;

        const hashedPassword = await bcrypt.hash(String(password), 10);

        const executiveData = {
          nombre,
          email: email.toLowerCase().trim(),
          cedula: cedula.toString().trim(),
          ciudadId,
          perfilVenta: perfil,
          role,
          salarioBase: salario,
          password: hashedPassword,
          activo: true
        };

        // Verificar si existe para actualizar o crear
        const existente = ejecutivos.find(e => e.email.toLowerCase() === email.toLowerCase().trim());

        if (existente) {
          // Si existe, no sobreescribimos el password a menos que venga en el excel
          if (!row.password && !row.Password && !row.contrasena) {
            delete executiveData.password;
          }
          await DatabaseManager.Ejecutivo.update(executiveData, { where: { id: existente.id } });
        } else {
          await DatabaseManager.Ejecutivo.create(executiveData);
        }
        successCount++;
      }

      message.success(`Importación finalizada: ${successCount} ejecutivos procesados, ${errorCount} errores.`);
      loadData();
    } catch (error) {
      console.error("Error importando ejecutivos:", error);
      message.error("Error al procesar el archivo: " + error.message);
    } finally {
      setLoading(false);
    }
    return false; // Prevent upload
  };

  const handleImportGruposCiudades = async (file) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      let successCount = 0;
      for (const row of jsonData) {
        const nombre = row.nombre || row.Nombre || row.Grupo;
        const descripcion = row.descripcion || row.Descripcion || '';

        if (!nombre) continue;

        const existente = gruposCiudades.find(g => g.nombre.toLowerCase().trim() === nombre.toLowerCase().trim());
        if (existente) {
          await DatabaseManager.GrupoCiudad.update({ nombre, descripcion }, { where: { id: existente.id } });
        } else {
          await DatabaseManager.GrupoCiudad.create({ nombre, descripcion });
        }
        successCount++;
      }
      message.success(`${successCount} grupos de ciudades procesados.`);
      loadData();
    } catch (error) {
      message.error("Error al importar grupos: " + error.message);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleImportAsociacionCiudades = async (file) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      let successCount = 0;
      let groupsFound = {};

      for (const row of jsonData) {
        const ciudadNombre = row.ciudad || row.Ciudad || row.Nombre;
        const grupoNombre = row.grupo || row.Grupo || row.Categoria;

        if (!ciudadNombre || !grupoNombre) continue;

        // Buscar grupo (caché local para velocidad)
        let grupo = groupsFound[grupoNombre.toLowerCase()];
        if (!grupo) {
          grupo = gruposCiudades.find(g => g.nombre.toLowerCase().trim() === grupoNombre.toLowerCase().trim());
          if (grupo) groupsFound[grupoNombre.toLowerCase()] = grupo;
        }

        if (!grupo) {
          console.warn(`Grupo no encontrado: ${grupoNombre} para la ciudad ${ciudadNombre}`);
          continue;
        }

        const existente = ciudades.find(c => c.nombre.toLowerCase().trim() === ciudadNombre.toLowerCase().trim());
        if (existente) {
          await DatabaseManager.Ciudad.update({ grupoCiudadId: grupo.id }, { where: { id: existente.id } });
        } else {
          await DatabaseManager.Ciudad.create({ nombre: ciudadNombre, grupoCiudadId: grupo.id });
        }
        successCount++;
      }
      message.success(`${successCount} asociaciones de ciudades procesadas.`);
      loadData();
    } catch (error) {
      message.error("Error al importar asociaciones: " + error.message);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleExportExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();

      // Hoja de Ejecutivos
      const ejecsSheet = XLSX.utils.json_to_sheet(ejecutivos.map(e => ({
        Nombre: e.nombre,
        Email: e.email,
        Rol: e.role,
        Ciudad: e.Ciudad?.nombre || 'N/A',
        Perfil: e.perfilVenta,
        SalarioBase: e.salarioBase,
        Activo: e.activo ? 'Sí' : 'No'
      })));
      XLSX.utils.book_append_sheet(workbook, ejecsSheet, 'Ejecutivos');

      // Hoja de Parámetros
      const paramsSheet = XLSX.utils.json_to_sheet(parameters);
      XLSX.utils.book_append_sheet(workbook, paramsSheet, 'Parámetros');

      // Hoja de Bandas Mensuales
      const bandsMSheet = XLSX.utils.json_to_sheet(bandasMensuales);
      XLSX.utils.book_append_sheet(workbook, bandsMSheet, 'Bandas Mensuales');

      XLSX.writeFile(workbook, `configuracion_comisiones_${new Date().getFullYear()}.xlsx`);
      message.success('Configuración exportada exitosamente');
    } catch (error) {
      message.error('Error al exportar: ' + error.message);
    }
  };


  const handleImportExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        // Process and import data
        processImportData(jsonData);
        message.success('Datos importados exitosamente');
      } catch (error) {
        message.error('Error al procesar archivo: ' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const processImportData = async (jsonData) => {
    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const row of jsonData) {
        const keys = Object.keys(row);
        const emailKey = keys.find(k => k.toLowerCase().includes('email'));
        const medioKey = keys.find(k => k.toLowerCase().includes('medio'));
        const anioKey = keys.find(k => k.toLowerCase().includes('año') || k.toLowerCase().includes('anio'));
        const mesKey = keys.find(k => k.toLowerCase().includes('mes'));
        const pptoKey = keys.find(k => k.toLowerCase().includes('presupuesto') || k.toLowerCase().includes('ppto'));
        const ventaKey = keys.find(k => k.toLowerCase().includes('venta'));
        const tipoKey = keys.find(k => k.toLowerCase().includes('tipo'));

        const email = emailKey ? row[emailKey]?.toString().trim() : null;
        const nombreMedio = medioKey ? row[medioKey]?.toString().trim() : null;
        const anio = anioKey ? parseInt(row[anioKey]) : null;
        const mes = mesKey ? parseInt(row[mesKey]) : null;
        const tipo = tipoKey ? row[tipoKey]?.toString().trim() : 'Base';

        if (!email || !nombreMedio || !anio || !mes) {
          console.log("Fila omitida por faltar datos clave:", row);
          errorCount++;
          continue;
        }

        const ejecutivo = ejecutivos.find(e => e.email?.toLowerCase().trim() === email.toLowerCase());
        let medio = medios.find(m => m.nombre?.toLowerCase().trim() === nombreMedio.toLowerCase());

        if (!medio && nombreMedio) {
          try {
            medio = await DatabaseManager.Medio.create({
              nombre: nombreMedio,
              mixAnual: 0.2,
              pesoEstrategico: 1.0
            });
            medios.push(medio);
          } catch (e) {
            console.error("Error al crear medio dinámicamente:", e);
          }
        }

        if (!ejecutivo || !medio) {
          errorCount++;
          continue;
        }

        const trimestre = Math.ceil(mes / 3);

        if (pptoKey) {
          const presupuestoVal = parseFloat(row[pptoKey]) || 0;
          const pptoExistente = await DatabaseManager.Presupuesto.findAll({
            where: { ejecutivoId: ejecutivo.id, medioId: medio.id, anio, mes, tipo }
          });

          if (pptoExistente && pptoExistente.length > 0) {
            await DatabaseManager.Presupuesto.update(
              { presupuesto: presupuestoVal, activo: true },
              { where: { id: pptoExistente[0].id } }
            );
          } else {
            await DatabaseManager.Presupuesto.create({
              ejecutivoId: ejecutivo.id,
              ciudadId: ejecutivo.ciudadId,
              medioId: medio.id,
              anio,
              mes,
              presupuesto: presupuestoVal,
              tipo,
              activo: true
            });
          }
        }

        if (ventaKey) {
          const ventaRealVal = parseFloat(row[ventaKey]) || 0;
          const ventaExistente = await DatabaseManager.Venta.findAll({
            where: { ejecutivoId: ejecutivo.id, medioId: medio.id, anio, mes }
          });

          if (ventaExistente && ventaExistente.length > 0) {
            await DatabaseManager.Venta.update(
              { ventaReal: ventaRealVal, trimestre, activo: true },
              { where: { id: ventaExistente[0].id } }
            );
          } else {
            await DatabaseManager.Venta.create({
              ejecutivoId: ejecutivo.id,
              ciudadId: ejecutivo.ciudadId,
              medioId: medio.id,
              anio,
              mes,
              trimestre,
              ventaReal: ventaRealVal,
              activo: true
            });
          }
        }

        successCount++;
      }

      message.success(`Importación finalizada. Éxitos: ${successCount}, Omitidos/Errores: ${errorCount}. (Ver consola para detalles si hubo errores)`);
      loadDatosCargados();
    } catch (error) {
      console.error('Error procesando datos:', error);
      message.error('Hubo un error al procesar las filas del Excel.');
    } finally {
      setLoading(false);
    }
  };

  const handleImportEstructuraExcel = (file) => {
    processImportEstructura(file);
    return false;
  };

  const processImportEstructura = async (file) => {
    setImporting(true);
    setImportProgress(0);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet);

      const totalFilas = jsonData.length;

      const mesMap = {
        'ENERO': 1, 'FEBRERO': 2, 'MARZO': 3, 'ABRIL': 4, 'MAYO': 5, 'JUNIO': 6,
        'JULIO': 7, 'AGOSTO': 8, 'SEPTIEMBRE': 9, 'OCTUBRE': 10, 'NOVIEMBRE': 11, 'DICIEMBRE': 12
      };

      // Mantener cachés locales para evitar duplicados en el mismo proceso
      const cacheGrupos = [...gruposCiudades];
      const cacheCiudades = [...ciudades];
      const cacheMedios = [...medios];
      const cacheEjecutivos = [...ejecutivos];
      const cacheTipos = [...tiposPresupuesto];

      let totalFilasImport = jsonData.length;
      let contadorFilas = 0;
      let presupuestosCreados = 0;
      let sumaTotalConsolidada = 0;

      for (const row of jsonData) {
        // Actualizar progreso (incluyendo filas saltadas)
        contadorFilas++;
        if (contadorFilas % 20 === 0 || contadorFilas === totalFilasImport) {
          setImportProgress(Math.round((contadorFilas / totalFilasImport) * 100));
        }

        // Normalizar keys
        const keys = Object.keys(row);

        // Solo la primera fila para no llenar la consola
        if (contadorFilas === 1) {
          console.log("[DEBUG] Encabezados detectados en el Excel:", keys);
        }

        const getVal = (searchKey) => {
          // 1. Buscar coincidencia exacta primero
          const exactKey = keys.find(key => key.toLowerCase().trim() === searchKey.toLowerCase().trim());
          if (exactKey) return row[exactKey]?.toString().trim();

          // 2. Si no hay exacta, buscar coincidencia parcial
          const partialKey = keys.find(key => key.toLowerCase().includes(searchKey.toLowerCase()));
          return partialKey ? row[partialKey]?.toString().trim() : null;
        };

        const regionalName = getVal('REGIONAL');
        const ciudadName = getVal('CIUDAD');
        const gerenteCiudadName = getVal('GERENTE CIUDAD');
        const gerenteVentasName = getVal('GERENTE VENTAS');
        const gerenteRegionalName = getVal('GERENTE REGIONAL');
        const ejecutivoName = getVal('EJECUTIVO');
        const medioName = getVal('MEDIO');
        const mesName = getVal('MES');
        const anioRow = getVal('ANO') || getVal('AÑO');
        const tipoRowName = getVal('TIPO PRESUPUESTO') || getVal('TIPO PRESU') || getVal('TIPO') || 'Base';
        const valorPpto = getVal('PRESUPUESTO') || getVal('VALOR');

        if (!regionalName || !ciudadName || !ejecutivoName || !medioName || !mesName || !valorPpto) {
          if (contadorFilas < 3) {
            console.log("[DEBUG] Fila saltada. Datos faltantes:", { regionalName, ciudadName, ejecutivoName, medioName, mesName, valorPpto });
          }
          continue;
        }
        // 1. Grupo Ciudad (Regional)
        let grupo = cacheGrupos.find(g => g.nombre.toLowerCase() === regionalName.toLowerCase());
        if (!grupo) {
          grupo = await DatabaseManager.GrupoCiudad.create({ nombre: regionalName, descripcion: 'Importado de Excel' });
          cacheGrupos.push(grupo);
        }

        // 2. Ciudad
        let ciudad = cacheCiudades.find(c => c.nombre.toLowerCase() === ciudadName.toLowerCase());
        if (!ciudad) {
          // Nota: Usamos grupoCiudadid o grupoCiudadId según DatabaseManager
          ciudad = await DatabaseManager.Ciudad.create({ nombre: ciudadName, grupoCiudadId: grupo.id });
          cacheCiudades.push(ciudad);
        }

        // 3. Medio
        let medio = cacheMedios.find(m => m.nombre.toLowerCase() === medioName.toLowerCase());
        if (!medio) {
          medio = await DatabaseManager.Medio.create({ nombre: medioName, mixAnual: 0.1, pesoEstrategico: 1.0 });
          cacheMedios.push(medio);
        }

        // 4. Tipo de Presupuesto
        let tipoObj = cacheTipos.find(t => t.nombre.toLowerCase() === tipoRowName.toLowerCase());
        if (!tipoObj) {
          tipoObj = await DatabaseManager.TipoPresupuesto.create({ nombre: tipoRowName, descripcion: 'Creado por importación' });
          cacheTipos.push(tipoObj);
        }

        // 5. Ejecutivos
        const upsertEjecutivo = async (nombre, role) => {
          if (!nombre) return null;
          let ej = cacheEjecutivos.find(e => e.nombre.toLowerCase() === nombre.toLowerCase());
          if (!ej) {
            const email = nombre.replace(/[^a-zA-Z0-9]/g, '.').toLowerCase() + '@rcn.com.co';
            ej = await DatabaseManager.Ejecutivo.create({
              nombre, email, cedula: 'C' + Math.floor(Math.random() * 100000000),
              ciudadId: ciudad.id, perfilVenta: role, role: 'user', salarioBase: 0, activo: true, password: 'password123'
            });
            cacheEjecutivos.push(ej);
          }
          return ej;
        };

        const objEjecutivo = await upsertEjecutivo(ejecutivoName, 'Ejecutivo');
        if (gerenteCiudadName) await upsertEjecutivo(gerenteCiudadName, 'Gerente Ciudad');
        if (gerenteVentasName) await upsertEjecutivo(gerenteVentasName, 'Gerente Ventas');
        if (gerenteRegionalName) await upsertEjecutivo(gerenteRegionalName, 'Gerente Regional');

        // 6. Presupuesto
        let mesNum = mesMap[mesName?.toString().toUpperCase().trim()];
        if (!mesNum && mesName) {
          // Intentar como número directo si no es texto
          const n = parseInt(mesName);
          if (n >= 1 && n <= 12) mesNum = n;
        }

        const anio = anioRow ? parseInt(anioRow) : (filtroAnio || new Date().getFullYear());

        // Limpieza de número ultra-robusta
        let valorFloat = 0;
        if (typeof valorPpto === 'number') {
          valorFloat = valorPpto;
        } else if (valorPpto) {
          // Eliminar moneda y espacios
          let cleanStr = valorPpto.toString().replace(/[$ \s]/g, '');
          // Caso 1: Formato 1.234.567,89 (CO/ES)
          if (cleanStr.includes('.') && cleanStr.includes(',')) {
            valorFloat = parseFloat(cleanStr.replace(/\./g, '').replace(',', '.'));
          }
          // Caso 2: Formato 1,234,567.89 (US/International)
          else if (cleanStr.includes(',') && cleanStr.includes('.') && cleanStr.indexOf(',') < cleanStr.indexOf('.')) {
            valorFloat = parseFloat(cleanStr.replace(/,/g, ''));
          }
          // Caso 3: Solo tiene coma (ej 7196421,46) -> asumimos decimal
          else if (cleanStr.includes(',') && !cleanStr.includes('.')) {
            valorFloat = parseFloat(cleanStr.replace(',', '.'));
          }
          // Caso 4: Solo tiene puntos (ej 7.196.421) -> asumimos miles
          else if (cleanStr.includes('.') && !cleanStr.includes(',')) {
            // Si el punto está cerca del final (ej 123.45), podría ser decimal
            const parts = cleanStr.split('.');
            if (parts[parts.length - 1].length === 2 || parts[parts.length - 1].length === 4) {
              valorFloat = parseFloat(cleanStr); // Probablemente decimal
            } else {
              valorFloat = parseFloat(cleanStr.replace(/\./g, '')); // Probablemente miles
            }
          } else {
            valorFloat = parseFloat(cleanStr);
          }
        }

        if (objEjecutivo && mesNum && !isNaN(valorFloat)) {
          sumaTotalConsolidada += valorFloat;
          // Usamos los nombres de columna camelCase de Supabase: ejecutivoId, ciudadId, medioId, tipoPresupuestoId
          const pptoExistente = await DatabaseManager.Presupuesto.findAll({
            where: {
              ejecutivoId: objEjecutivo.id,
              medioId: medio.id,
              anio,
              mes: mesNum,
              tipoPresupuestoId: tipoObj.id
            }
          });

          if (pptoExistente && pptoExistente.length > 0) {
            await DatabaseManager.Presupuesto.update(
              { presupuesto: valorFloat, activo: true },
              { where: { id: pptoExistente[0].id } }
            );
          } else {
            await DatabaseManager.Presupuesto.create({
              ejecutivoId: objEjecutivo.id,
              ciudadId: ciudad.id,
              medioId: medio.id,
              anio,
              mes: mesNum,
              presupuesto: valorFloat,
              tipoPresupuestoId: tipoObj.id,
              activo: true
            });
            if (contadorFilas < 3) {
              console.log("[DEBUG] Creado presupuesto para:", objEjecutivo.nombre);
            }
          }
          presupuestosCreados++;
        } else {
          if (contadorFilas < 3) {
            console.log("[DEBUG] Error de variables antes de guardar:", { objEjecutivo, mesNum, valorFloat });
          }
        }
      }

      console.log(`[DEBUG] Finalizado. Procesadas: ${filasProcesadas}, Guardadas: ${presupuestosCreados}`);
      console.log(`[DEBUG] Suma total consolidada en importación: $${sumaTotalConsolidada.toLocaleString('es-CO', { minimumFractionDigits: 2 })}`);

      message.success({
        content: `Importación finalizada: ${filasProcesadas} filas procesadas. Total: $${sumaTotalConsolidada.toLocaleString('es-CO')}`,
        key: 'importEstructura',
        duration: 10
      });

      // Actualizar estados finales
      setGruposCiudades(cacheGrupos);
      setCiudades(cacheCiudades);
      setMedios(cacheMedios);
      setEjecutivos(cacheEjecutivos);

      loadDatosCargados();
    } catch (error) {
      console.error('Error importando estructura:', error);
      message.error({ content: 'Error en importación: ' + error.message, key: 'importEstructura' });
    } finally {
      setLoading(false);
    }
  };

  const calculateCommissions = async () => {
    setLoading(true);
    try {
      const calculator = new CommissionCalculator();
      const year = filtroAnio;

      let monthsToCalculate = [];
      if (filtroMes) {
        monthsToCalculate = [filtroMes];
      } else if (filtroTrimestre) {
        monthsToCalculate = [filtroTrimestre * 3 - 2, filtroTrimestre * 3 - 1, filtroTrimestre * 3];
      } else {
        monthsToCalculate = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      }

      for (const ejecutivo of ejecutivos) {
        for (const mes of monthsToCalculate) {
          const currentQuarter = Math.ceil(mes / 3);
          for (const medio of medios) {
            await calculator.calcularComisionMensual(
              ejecutivo.id,
              ejecutivo.ciudadId,
              medio.id,
              year,
              mes
            );

            if (mes % 3 === 0) {
              await calculator.calcularComisionTrimestral(
                ejecutivo.id,
                ejecutivo.ciudadId,
                medio.id,
                year,
                currentQuarter
              );
            }
          }
        }
      }

      message.success(`Cálculos actualizados para ${monthsToCalculate.length} mes(es) del año ${year}`);
      loadCommissionSummary();
    } catch (error) {
      console.error('Error al calcular:', error);
      message.error('Error al calcular comisiones: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const parameterColumns = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'Tipo Presupuesto',
      dataIndex: 'tipoPresupuestoId',
      key: 'tipo',
      render: (id, record) => {
        const tp = tiposPresupuesto.find(t => t.id === id);
        return tp ? tp.nombre : (record.tipo || 'General');
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleEdit(record, 'parameter')}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDelete(record, 'parameter')}
          >
            Borrar
          </Button>
        </Space>
      ),
    },
  ];

  const bandaColumns = (type) => [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
    },
    {
      title: 'Desde',
      dataIndex: 'desde',
      key: 'desde',
      render: (value) => `${(value * 100).toFixed(2)}%`,
    },
    {
      title: 'Hasta',
      dataIndex: 'hasta',
      key: 'hasta',
      render: (value) => `${(value * 100).toFixed(2)}%`,
    },
    {
      title: 'Factor Individual',
      dataIndex: 'factorIndividual',
      key: 'factorIndividual',
    },
    {
      title: 'Factor Integral',
      dataIndex: 'factorIntegral',
      key: 'factorIntegral',
    },
    {
      title: 'Tipo Presupuesto',
      dataIndex: 'tipoPresupuestoId',
      key: 'tipo',
      render: (id, record) => {
        const tp = tiposPresupuesto.find(t => t.id === id);
        return tp ? tp.nombre : (record.tipo || 'General');
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleEdit(record, type === 'mensual' ? 'bandaMensual' : 'bandaTrimestral')}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDelete(record, type === 'mensual' ? 'bandaMensual' : 'bandaTrimestral')}
          >
            Borrar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#001529', padding: '0 20px' }}>
        <div style={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '20px' }}>
            Panel de Administración - Simulador de Comisiones RCN
          </h1>
          <div>
            <span style={{ marginRight: '20px' }}>
              {user.nombre} (Administrador)
            </span>
            <span style={{ color: '#52c41a' }}>
              {user.ciudad}
            </span>
          </div>
        </div>
      </Header>

      <Layout>
        <Sider width={200} style={{ background: '#fff' }}>
          <Menu
            mode="inline"
            selectedKeys={[activeTab]}
            style={{ height: '100%' }}
            onClick={(e) => setActiveTab(e.key)}
          >
            <Menu.Item key="parameters" icon={<SettingOutlined />}>
              Parámetros
            </Menu.Item>

            <Menu.Item key="geography" icon={<GlobalOutlined />}>
              Geografía
            </Menu.Item>

            <Menu.Item key="executives" icon={<UserOutlined />}>
              Ejecutivos
            </Menu.Item>

            <Menu.Item key="import" icon={<UploadOutlined />}>
              Importar Excel
            </Menu.Item>
            <Menu.Item key="datos" icon={<BarChartOutlined />}>
              Datos Cargados
            </Menu.Item>
            <Menu.Item key="simulator" icon={<CalculatorOutlined />}>
              Simulador 🎮
            </Menu.Item>
            <Menu.Item key="export" icon={<DownloadOutlined />}>
              Exportar
            </Menu.Item>
          </Menu>
        </Sider>

        <Content style={{ padding: '20px', background: '#f0f2f5' }}>

          <Card>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarExtraContent={
                importing ? (
                  <div style={{ width: 300, marginRight: 20 }}>
                    <Text type="secondary" size="small">Importando datos: {importProgress}%</Text>
                    <Progress percent={importProgress} status="active" size="small" showInfo={false} />
                  </div>
                ) : (
                  <Button
                    type="primary"
                    loading={loading}
                    onClick={calculateCommissions}
                  >
                    Recalcular Comisiones
                  </Button>
                )
              }
            >
              <TabPane tab="Parámetros" key="parameters">
                <Alert
                  message="Configuración del Sistema"
                  description="Aquí puedes ajustar los parámetros del cálculo de comisiones divididos por categorías."
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                <Space style={{ marginBottom: '16px' }}>
                  <Button
                    type="primary"
                    icon={<SettingOutlined />}
                    onClick={() => handleAdd('parameter')}
                  >
                    Nuevo Parámetro
                  </Button>
                  <Button
                    icon={<GlobalOutlined />}
                    onClick={() => handleAdd('tipoPresupuesto')}
                  >
                    Gestionar Tipos de Ppto
                  </Button>
                </Space>

                <Tabs
                  type="editable-card"
                  activeKey={activeBudgetTab}
                  onChange={setActiveBudgetTab}
                  onEdit={(targetKey, action) => {
                    if (action === 'add') handleAdd('tipoPresupuesto');
                  }}
                >
                  {tiposPresupuesto.map(tp => (
                    <TabPane tab={tp.nombre} key={tp.id.toString()}>
                      <Alert
                        message={`Configuración para: ${tp.nombre}`}
                        description={tp.descripcion || 'Sin descripción'}
                        type="success"
                        showIcon
                        style={{ marginBottom: '16px' }}
                        action={
                          <Button size="small" type="link" onClick={() => handleEdit(tp, 'tipoPresupuesto')}>
                            Editar Tipo
                          </Button>
                        }
                      />

                      <Tabs defaultActiveKey="1" type="card">
                        <TabPane tab="Básicos" key="1">
                          <Table
                            columns={parameterColumns}
                            dataSource={parameters.filter(p =>
                              (p.tipoPresupuestoId === tp.id || (!p.tipoPresupuestoId && tp.nombre === 'Base')) &&
                              !p.nombre.toLowerCase().includes('balance') &&
                              !p.nombre.toLowerCase().includes('factor') &&
                              !p.nombre.toLowerCase().includes('umbral') &&
                              !p.nombre.toLowerCase().includes('compuerta')
                            )}
                            rowKey="id"
                            loading={loading}
                            pagination={false}
                          />
                        </TabPane>

                        <TabPane tab="Balance Trimestral" key="3">
                          <Table
                            columns={parameterColumns}
                            dataSource={parameters.filter(p =>
                              (p.tipoPresupuestoId === tp.id || (!p.tipoPresupuestoId && tp.nombre === 'Base')) &&
                              p.nombre.toLowerCase().includes('balance')
                            )}
                            rowKey="id"
                            loading={loading}
                            pagination={false}
                          />
                        </TabPane>
                        <TabPane tab="Factores y Umbrales" key="4">
                          <Table
                            columns={parameterColumns}
                            dataSource={parameters.filter(p =>
                              (p.tipoPresupuestoId === tp.id || (!p.tipoPresupuestoId && tp.nombre === 'Base')) &&
                              (p.nombre.toLowerCase().includes('factor') || p.nombre.toLowerCase().includes('umbral') || p.nombre.toLowerCase().includes('multiplicador') || p.nombre.toLowerCase().includes('compuerta'))
                            )}
                            rowKey="id"
                            loading={loading}
                            pagination={false}
                          />
                        </TabPane>
                        <TabPane tab="Bandas de Comisión" key="5">
                          <Tabs defaultActiveKey="mensuales" type="line">
                            <TabPane tab="Mensuales" key="mensuales">
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleAdd('bandaMensual', { tipoPresupuestoId: tp.id })}
                                style={{ marginBottom: '16px', marginTop: '8px' }}
                              >
                                Nueva Banda Mensual
                              </Button>
                              <Table
                                columns={bandaColumns('mensual')}
                                dataSource={bandasMensuales.filter(b => b.tipoPresupuestoId === tp.id || (!b.tipoPresupuestoId && tp.nombre === 'Base'))}
                                rowKey="id"
                                loading={loading}
                                size="small"
                              />
                            </TabPane>
                            <TabPane tab="Trimestrales" key="trimestrales">
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleAdd('bandaTrimestral', { tipoPresupuestoId: tp.id })}
                                style={{ marginBottom: '16px', marginTop: '8px' }}
                              >
                                Nueva Banda Trimestral
                              </Button>
                              <Table
                                columns={bandaColumns('trimestral')}
                                dataSource={bandasTrimestrales.filter(b => b.tipoPresupuestoId === tp.id || (!b.tipoPresupuestoId && tp.nombre === 'Base'))}
                                rowKey="id"
                                loading={loading}
                                size="small"
                              />
                            </TabPane>
                          </Tabs>
                        </TabPane>
                      </Tabs>
                    </TabPane>
                  ))}
                </Tabs>
              </TabPane>

              <TabPane tab="Geografía y Agrupaciones" key="geography">
                <Row gutter={16}>
                  <Col span={10}>
                    <Card
                      title="Grupos de Ciudades"
                      extra={
                        <Space>
                          <Upload
                            accept=".xlsx,.xls"
                            beforeUpload={handleImportGruposCiudades}
                            showUploadList={false}
                          >
                            <Button icon={<UploadOutlined />} size="small">Importar</Button>
                          </Upload>
                          <Button type="primary" size="small" onClick={() => handleAdd('grupoCiudad')}>Nuevo Grupo</Button>
                        </Space>
                      }
                    >
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        <InfoCircleOutlined /> Columnas: <b>Nombre</b>, <b>Descripcion</b> (opcional)
                      </Text>
                      <Table
                        size="small"
                        dataSource={gruposCiudades}
                        rowKey="id"
                        columns={[
                          { title: 'Nombre', dataIndex: 'nombre', sorter: (a, b) => a.nombre.localeCompare(b.nombre) },
                          { title: 'Descripción', dataIndex: 'descripcion' },
                          {
                            title: 'Acciones', render: (_, r) => (
                              <Space>
                                <Button type="link" size="small" onClick={() => handleEdit(r, 'grupoCiudad')}>Editar</Button>
                                <Button type="link" danger size="small" onClick={() => handleDelete(r, 'grupoCiudad')}>X</Button>
                              </Space>
                            )
                          }
                        ]}
                      />
                    </Card>
                  </Col>
                  <Col span={14}>
                    <Card
                      title="Asociación Ciudades"
                      extra={
                        <Space>
                          <Upload
                            accept=".xlsx,.xls"
                            beforeUpload={handleImportAsociacionCiudades}
                            showUploadList={false}
                          >
                            <Button icon={<UploadOutlined />} size="small">Importar</Button>
                          </Upload>
                          <Button type="primary" size="small" onClick={() => handleAdd('ciudad')}>Nueva Ciudad</Button>
                        </Space>
                      }
                    >
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                        <InfoCircleOutlined /> Columnas: <b>Ciudad</b>, <b>Grupo</b>
                      </Text>
                      <Table
                        size="small"
                        dataSource={ciudades}
                        rowKey="id"
                        columns={[
                          { title: 'Ciudad', dataIndex: 'nombre', sorter: (a, b) => a.nombre.localeCompare(b.nombre) },
                          { title: 'Grupo', dataIndex: ['GrupoCiudad', 'nombre'], sorter: (a, b) => (a.GrupoCiudad?.nombre || '').localeCompare(b.GrupoCiudad?.nombre || '') },
                          {
                            title: 'Acciones', render: (_, r) => (
                              <Space>
                                <Button type="link" size="small" onClick={() => handleEdit(r, 'ciudad')}>Editar</Button>
                                <Button type="link" danger size="small" onClick={() => handleDelete(r, 'ciudad')}>X</Button>
                              </Space>
                            )
                          }
                        ]}
                      />
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Ejecutivos" key="executives">
                <Space style={{ marginBottom: '16px' }}>
                  <Button
                    type="primary"
                    icon={<UserOutlined />}
                    onClick={() => handleAdd('executive')}
                  >
                    Nuevo Ejecutivo
                  </Button>
                  <Upload
                    accept=".xlsx,.xls,.csv"
                    beforeUpload={handleImportEjecutivos}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>
                      Carga Masiva Ejecutivos
                    </Button>
                  </Upload>
                  <Text type="secondary" style={{ marginLeft: '10px' }}>
                    <InfoCircleOutlined /> Columnas requeridas: Nombre, Email, Cedula, Ciudad, Perfil, Salario, Password (opcional)
                  </Text>
                </Space>

                <Table
                  columns={[
                    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', sorter: (a, b) => a.nombre.localeCompare(b.nombre) },
                    { title: 'Cédula', dataIndex: 'cedula', key: 'cedula' },
                    { title: 'Email', dataIndex: 'email', key: 'email', sorter: (a, b) => a.email.localeCompare(b.email) },
                    {
                      title: 'Rol', dataIndex: 'role', key: 'role',
                      sorter: (a, b) => a.role.localeCompare(b.role),
                      render: (role) => <Tag color={role === 'admin' ? 'red' : 'blue'}>{role}</Tag>
                    },
                    { title: 'Ciudad', dataIndex: ['Ciudad', 'nombre'], key: 'ciudad', sorter: (a, b) => (a.Ciudad?.nombre || '').localeCompare(b.Ciudad?.nombre || '') },
                    {
                      title: 'Perfil', dataIndex: 'perfilVenta', key: 'perfilVenta',
                      sorter: (a, b) => (a.perfilVenta || '').localeCompare(b.perfilVenta || ''),
                      render: (perfil) => <Tag color="orange">{perfil}</Tag>
                    },
                    {
                      title: 'Salario Base', dataIndex: 'salarioBase', key: 'salarioBase',
                      sorter: (a, b) => (a.salarioBase || 0) - (b.salarioBase || 0),
                      render: (value) => value ? `$${value.toLocaleString('es-CO')}` : '$0'
                    },
                    {
                      title: 'Activo', dataIndex: 'activo', key: 'activo',
                      sorter: (a, b) => (a.activo === b.activo) ? 0 : a.activo ? -1 : 1,
                      render: (activo) => <Tag color={activo ? 'green' : 'red'}>{activo ? 'Sí' : 'No'}</Tag>
                    },
                    {
                      title: 'Acciones',
                      key: 'actions',
                      render: (_, record) => (
                        <Space>
                          <Button
                            type="link"
                            onClick={() => handleEdit(record, 'executive')}
                          >
                            Editar
                          </Button>
                          <Button
                            type="link"
                            danger
                            onClick={() => handleDelete(record, 'executive')}
                          >
                            Borrar
                          </Button>
                        </Space>
                      )
                    }
                  ]}
                  dataSource={ejecutivos}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                />
              </TabPane>



              <TabPane tab="Importar" key="import">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Card title="Importación Inteligente de Datos">
                    <p>El sistema detectará automáticamente si estás subiendo <b>Presupuestos</b>, <b>Ventas Reales</b> o ambos, basándose en los encabezados de tu archivo.</p>

                    <Row gutter={16}>
                      <Col span={8}>
                        <Alert
                          message="Opción A: Solo Presupuestos"
                          description={
                            <ul style={{ paddingLeft: 20 }}>
                              <li><b>Encabezados:</b> Email, Medio, Año, Mes, Presupuesto, Tipo (opcional)</li>
                              <li>Ideal para metas a inicio de mes.</li>
                            </ul>
                          }
                          type="info"
                        />
                      </Col>
                      <Col span={8}>
                        <Alert
                          message="Opción B: Solo Ventas Reales"
                          description={
                            <ul style={{ paddingLeft: 20 }}>
                              <li><b>Encabezados:</b> Email, Medio, Año, Mes, Venta Real</li>
                              <li>Ideal para cierres de mes.</li>
                            </ul>
                          }
                          type="success"
                        />
                      </Col>
                      <Col span={8}>
                        <Alert
                          message="Opción C: Estructura Completa"
                          description={
                            <ul style={{ paddingLeft: 20 }}>
                              <li><b>Encabezados:</b> Regional, Ciudad, Gerentes, Ejecutivo, Medio, Mes, Ano, Tipo Presupuesto, Presupuesto</li>
                              <li>Crea la estructura de usuarios y asigna presupuestos.</li>
                            </ul>
                          }
                          type="warning"
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <div style={{ textAlign: 'center', padding: '20px 0', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                      <div>
                        <Upload
                          accept=".xlsx,.xls,.csv"
                          beforeUpload={handleImportExcel}
                          showUploadList={false}
                        >
                          <Button type="primary" icon={<UploadOutlined />} size="large" style={{ height: 60, padding: '0 40px', fontSize: 18 }}>
                            Subir Presupuestos o Ventas (A/B)
                          </Button>
                        </Upload>
                        <p style={{ marginTop: 10, color: '#666' }}>Soporta .xlsx, .xls y .csv</p>
                      </div>
                      <div>
                        <Upload
                          accept=".xlsx,.xls,.csv"
                          beforeUpload={handleImportEstructuraExcel}
                          showUploadList={false}
                        >
                          <Button type="primary" danger icon={<UploadOutlined />} size="large" style={{ height: 60, padding: '0 40px', fontSize: 18 }}>
                            Subir Estructura Completa (C)
                          </Button>
                        </Upload>
                        <p style={{ marginTop: 10, color: '#666' }}>Soporta .xlsx, .xls y .csv</p>
                      </div>
                    </div>
                  </Card>
                </Space>
              </TabPane>

              <TabPane tab="Datos Cargados" key="datos">
                <Card title="Administración y Resumen de Datos">
                  <Row gutter={16} style={{ marginBottom: 20 }}>
                    <Col span={8}>
                      <Card size="small" style={{ background: '#f0f5ff' }}>
                        <Statistic
                          title="Presupuesto Total (Filtrado)"
                          value={datosCargados.presupuestos
                            .filter(p =>
                              (!filtroEjecutivo || p.ejecutivoId === filtroEjecutivo) &&
                              (!filtroMes || p.mes === filtroMes) &&
                              (!filtroAnio || p.anio === filtroAnio) &&
                              (!filtroTrimestre || Math.ceil(p.mes / 3) === filtroTrimestre)
                            )
                            .reduce((sum, p) => sum + (p.presupuesto || 0), 0)
                          }
                          prefix="$"
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small" style={{ background: '#f6ffed' }}>
                        <Statistic
                          title="Venta Total (Filtrada)"
                          value={datosCargados.ventas
                            .filter(v =>
                              (!filtroEjecutivo || v.ejecutivoId === filtroEjecutivo) &&
                              (!filtroMes || v.mes === filtroMes) &&
                              (!filtroAnio || v.anio === filtroAnio) &&
                              (!filtroTrimestre || Math.ceil(v.mes / 3) === filtroTrimestre)
                            )
                            .reduce((sum, v) => sum + (v.ventaReal || 0), 0)
                          }
                          prefix="$"
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small" style={{ background: '#fff7e6' }}>
                        {(() => {
                          const ppto = datosCargados.presupuestos
                            .filter(p =>
                              (!filtroEjecutivo || p.ejecutivoId === filtroEjecutivo) &&
                              (!filtroMes || p.mes === filtroMes) &&
                              (!filtroAnio || p.anio === filtroAnio) &&
                              (!filtroTrimestre || Math.ceil(p.mes / 3) === filtroTrimestre)
                            )
                            .reduce((sum, p) => sum + (p.presupuesto || 0), 0);
                          const vta = datosCargados.ventas
                            .filter(v =>
                              (!filtroEjecutivo || v.ejecutivoId === filtroEjecutivo) &&
                              (!filtroMes || v.mes === filtroMes) &&
                              (!filtroAnio || v.anio === filtroAnio) &&
                              (!filtroTrimestre || Math.ceil(v.mes / 3) === filtroTrimestre)
                            )
                            .reduce((sum, v) => sum + (v.ventaReal || 0), 0);
                          const cumpli = ppto > 0 ? (vta / ppto) * 100 : 0;
                          return <Statistic title="Cumplimiento Global" value={cumpli} suffix="%" precision={1} />;
                        })()}
                      </Card>
                    </Col>
                  </Row>

                  <Space style={{ marginBottom: 20, flexWrap: 'wrap' }}>
                    <Select
                      placeholder="Ejecutivo"
                      allowClear
                      style={{ width: 200 }}
                      onChange={setFiltroEjecutivo}
                      value={filtroEjecutivo}
                    >
                      {ejecutivos.map(e => <Option key={e.id} value={e.id}>{e.nombre}</Option>)}
                    </Select>
                    <Select
                      placeholder="Mes"
                      allowClear
                      style={{ width: 100 }}
                      onChange={(val) => { setFiltroMes(val); if (val) setFiltroTrimestre(null); }}
                      value={filtroMes}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => <Option key={m} value={m}>{m}</Option>)}
                    </Select>
                    <Select
                      placeholder="Trimestre"
                      allowClear
                      style={{ width: 110 }}
                      onChange={(val) => { setFiltroTrimestre(val); if (val) setFiltroMes(null); }}
                      value={filtroTrimestre}
                    >
                      <Option value={1}>Trim. 1</Option>
                      <Option value={2}>Trim. 2</Option>
                      <Option value={3}>Trim. 3</Option>
                      <Option value={4}>Trim. 4</Option>
                    </Select>
                    <Select
                      placeholder="Año"
                      style={{ width: 90 }}
                      onChange={setFiltroAnio}
                      value={filtroAnio}
                    >
                      {[2024, 2025, 2026].map(y => <Option key={y} value={y}>{y}</Option>)}
                    </Select>
                    <Button onClick={loadDatosCargados} icon={<SettingOutlined />}>Actualizar</Button>
                    <Button danger onClick={handleVaciarTodo} icon={<DeleteOutlined />}>Vaciar Base de Datos</Button>
                    {(filtroEjecutivo || filtroMes || filtroAnio || filtroTrimestre) && (
                      <Button onClick={() => { setFiltroEjecutivo(null); setFiltroMes(null); setFiltroTrimestre(null); setFiltroAnio(new Date().getFullYear()); }}>
                        Limpiar Filtros
                      </Button>
                    )}
                  </Space>

                  <Tabs defaultActiveKey="ppto_sub" type="line">
                    <TabPane tab="Presupuestos" key="ppto_sub">
                      <Table
                        size="small"
                        columns={[
                          {
                            title: 'Año', dataIndex: 'anio', key: 'anio', width: 80,
                            filters: [2024, 2025, 2026].map(y => ({ text: y, value: y })),
                            onFilter: (value, record) => record.anio === value,
                          },
                          {
                            title: 'Mes', dataIndex: 'mes', key: 'mes', width: 80,
                            filters: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => ({ text: m, value: m })),
                            onFilter: (value, record) => record.mes === value,
                          },
                          {
                            title: 'Ejecutivo', dataIndex: ['Ejecutivo', 'nombre'], key: 'ejecutivo',
                            filters: ejecutivos.map(e => ({ text: e.nombre, value: e.nombre })),
                            filterSearch: true,
                            onFilter: (value, record) => record.Ejecutivo?.nombre === value,
                          },
                          {
                            title: 'Medio', dataIndex: ['Medio', 'nombre'], key: 'medio',
                            filters: medios.map(m => ({ text: m.nombre, value: m.nombre })),
                            filterSearch: true,
                            onFilter: (value, record) => record.Medio?.nombre === value,
                          },
                          {
                            title: 'Tipo', dataIndex: ['TipoPresupuesto', 'nombre'], key: 'tipo',
                            render: t => <Tag color="cyan">{t || 'Base'}</Tag>
                          },
                          { title: 'Presupuesto', dataIndex: 'presupuesto', sorter: (a, b) => a.presupuesto - b.presupuesto, render: v => `$${v?.toLocaleString('es-CO')}` },
                          {
                            title: 'Acciones', key: 'ops', render: (_, record) => (
                              <Space>
                                <Button type="link" onClick={() => handleEdit(record, 'presupuesto')}>Editar</Button>
                                <Button type="link" danger onClick={() => handleDelete(record, 'presupuesto')}>Eliminar</Button>
                              </Space>
                            )
                          }
                        ]}
                        dataSource={datosCargados.presupuestos.filter(p =>
                          (!filtroEjecutivo || p.ejecutivoId === filtroEjecutivo) &&
                          (!filtroMes || p.mes === filtroMes) &&
                          (!filtroAnio || p.anio === filtroAnio) &&
                          (!filtroTrimestre || Math.ceil(p.mes / 3) === filtroTrimestre)
                        )}
                        rowKey="id"
                        pagination={{ pageSize: 15 }}
                      />
                    </TabPane>
                    <TabPane tab="Ventas Reales" key="ventas_sub">
                      <Table
                        size="small"
                        columns={[
                          {
                            title: 'Año', dataIndex: 'anio', key: 'anio', width: 80,
                            filters: [2024, 2025, 2026].map(y => ({ text: y, value: y })),
                            onFilter: (value, record) => record.anio === value,
                          },
                          {
                            title: 'Mes', dataIndex: 'mes', key: 'mes', width: 80,
                            filters: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => ({ text: m, value: m })),
                            onFilter: (value, record) => record.mes === value,
                          },
                          {
                            title: 'Ejecutivo', dataIndex: ['Ejecutivo', 'nombre'], key: 'ejecutivo',
                            filters: ejecutivos.map(e => ({ text: e.nombre, value: e.nombre })),
                            filterSearch: true,
                            onFilter: (value, record) => record.Ejecutivo?.nombre === value,
                          },
                          {
                            title: 'Medio', dataIndex: ['Medio', 'nombre'], key: 'medio',
                            filters: medios.map(m => ({ text: m.nombre, value: m.nombre })),
                            filterSearch: true,
                            onFilter: (value, record) => record.Medio?.nombre === value,
                          },
                          { title: 'Venta Real', dataIndex: 'ventaReal', sorter: (a, b) => a.ventaReal - b.ventaReal, render: v => `$${v?.toLocaleString('es-CO')}` },
                          {
                            title: 'Acciones', key: 'ops', render: (_, record) => (
                              <Space>
                                <Button type="link" onClick={() => handleEdit(record, 'venta')}>Editar</Button>
                                <Button type="link" danger onClick={() => handleDelete(record, 'venta')}>Eliminar</Button>
                              </Space>
                            )
                          }
                        ]}
                        dataSource={datosCargados.ventas.filter(v =>
                          (!filtroEjecutivo || v.ejecutivoId === filtroEjecutivo) &&
                          (!filtroMes || v.mes === filtroMes) &&
                          (!filtroAnio || v.anio === filtroAnio) &&
                          (!filtroTrimestre || Math.ceil(v.mes / 3) === filtroTrimestre)
                        )}
                        rowKey="id"
                        pagination={{ pageSize: 15 }}
                      />
                    </TabPane>
                  </Tabs>
                </Card>
              </TabPane>


              <TabPane tab="Simulador 🎮" key="simulator">
                <InteractiveSimulator currentUser={user} isAdmin={true} />
              </TabPane>

              <TabPane tab="Exportar" key="export">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Card>
                    <Title level={4}>Exportar Configuración</Title>
                    <p>Descarga la configuración actual del sistema en formato Excel.</p>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={handleExportExcel}
                    >
                      Exportar Configuración
                    </Button>
                  </Card>
                </Space>
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>

      <Modal
        title={
          modalType === 'parameter' ? (editingRecord ? 'Editar Parámetro' : 'Nuevo Parámetro') :
            modalType === 'bandaMensual' ? (editingRecord ? 'Editar Banda Mensual' : 'Nueva Banda Mensual') :
              modalType === 'bandaTrimestral' ? (editingRecord ? 'Editar Banda Trimestral' : 'Nueva Banda Trimestral') :
                modalType === 'presupuesto' ? 'Editar Presupuesto' :
                  modalType === 'venta' ? 'Editar Venta Real' :
                    modalType === 'medio' ? (editingRecord ? 'Editar Medio' : 'Nuevo Medio') :
                      modalType === 'tipoPresupuesto' ? (editingRecord ? 'Editar Tipo de Presupuesto' : 'Nuevo Tipo de Presupuesto') :
                        (editingRecord ? 'Editar Ejecutivo' : 'Nuevo Ejecutivo')
        }
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          {modalType === 'tipoPresupuesto' && (
            <>
              <Form.Item label="Nombre del Tipo" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: Base, Ppto Adicional" />
              </Form.Item>
              <Form.Item label="Descripción" name="descripcion">
                <Input />
              </Form.Item>
              <Form.Item label="Medios Involucrados" name="mediosInvolucrados">
                <Select mode="multiple" placeholder="Seleccionar Medios">
                  {medios.map(m => (
                    <Select.Option key={m.id} value={m.id}>{m.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Cobertura Geográfica (Ciudades/Regionales)" name="ciudadesInvolucradas">
                <Select mode="multiple" placeholder="Seleccionar Ciudades Aplicables">
                  {gruposCiudades.map(grupo => (
                    <Select.OptGroup key={grupo.id} label={grupo.nombre}>
                      {ciudades.filter(c => c.grupoCiudadId === grupo.id).map(ciudad => (
                        <Select.Option key={ciudad.id} value={ciudad.id}>{ciudad.nombre}</Select.Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {modalType === 'grupoCiudad' && (
            <>
              <Form.Item label="Nombre del Grupo" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: Bogotá, Regionales, Costa" />
              </Form.Item>
              <Form.Item label="Descripción" name="descripcion">
                <Input />
              </Form.Item>
            </>
          )}

          {modalType === 'ciudad' && (
            <>
              <Form.Item label="Nombre de la Ciudad" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: Medellín" />
              </Form.Item>
              <Form.Item label="Grupo de Ciudades" name="grupoCiudadId" rules={[{ required: true }]}>
                <Select placeholder="Seleccionar Grupo">
                  {gruposCiudades.map(g => (
                    <Select.Option key={g.id} value={g.id}>{g.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {modalType === 'parameter' && (
            <>
              <Form.Item label="Nombre" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: multiplicador_pautas" />
              </Form.Item>
              <Form.Item label="Valor" name="valor" rules={[{ required: true }]}>
                <Input placeholder="1.2" />
              </Form.Item>
              <Form.Item label="Tipo de Presupuesto" name="tipoPresupuestoId">
                <Select placeholder="Seleccionar Tipo">
                  <Select.Option value={null}>General (Todos)</Select.Option>
                  {tiposPresupuesto.map(tp => (
                    <Select.Option key={tp.id} value={tp.id}>{tp.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Descripción" name="descripcion">
                <Input />
              </Form.Item>
            </>
          )}

          {(modalType === 'bandaMensual' || modalType === 'bandaTrimestral') && (
            <>
              <Form.Item label="Nombre/Nivel" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: Nivel 1" />
              </Form.Item>
              <Form.Item label="Desde (0.0 a 1.0)" name="desde" rules={[{ required: true }]}>
                <InputNumber min={0} max={2} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Hasta (0.0 a 1.0)" name="hasta" rules={[{ required: true }]}>
                <InputNumber min={0} max={2} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Factor Individual" name="factorIndividual" rules={[{ required: true }]}>
                <InputNumber min={0} max={5} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Factor Integral" name="factorIntegral" rules={[{ required: true }]}>
                <InputNumber min={0} max={5} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Tipo de Presupuesto" name="tipoPresupuestoId">
                <Select placeholder="Seleccionar Tipo">
                  {tiposPresupuesto.map(tp => (
                    <Select.Option key={tp.id} value={tp.id}>{tp.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Grupo Ciudad" name="grupoCiudadId">
                <Select placeholder="Seleccionar Grupo">
                  <Select.Option value={null}>Global (Todas)</Select.Option>
                  {gruposCiudades.map(g => (
                    <Select.Option key={g.id} value={g.id}>{g.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {modalType === 'executive' && (
            <>
              <Form.Item label="Nombre" name="nombre" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Cédula" name="cedula" rules={[{ required: true }]}>
                <Input placeholder="Número de documento" />
              </Form.Item>
              <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                <Input />
              </Form.Item>
              <Form.Item
                label={editingRecord ? "Nueva Contraseña (dejar en blanco para no cambiar)" : "Contraseña"}
                name="password"
                rules={[{ required: !editingRecord, message: 'La contraseña es obligatoria' }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item label="Rol" name="role" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="admin">Administrador</Select.Option>
                  <Select.Option value="employee">Ejecutivo de Ventas</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Ciudad" name="ciudadId" rules={[{ required: true }]}>
                <Select placeholder="Seleccionar Ciudad">
                  {ciudades.map(c => (
                    <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Perfil de Venta" name="perfilVenta">
                <Select placeholder="Seleccionar Perfil">
                  <Select.Option value="Ejecutivo">Ejecutivo</Select.Option>
                  <Select.Option value="Gerente Regional">Gerente Regional</Select.Option>
                  <Select.Option value="Gerente de Ventas Regional">Gerente de Ventas Regional</Select.Option>
                  <Select.Option value="VIP">VIP</Select.Option>
                  <Select.Option value="Intermedio">Intermedio</Select.Option>
                  <Select.Option value="Local">Local</Select.Option>
                  <Select.Option value="N/A">N/A</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Salario Base" name="salarioBase">
                <InputNumber style={{ width: '100%' }} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
              </Form.Item>
            </>
          )}

          {modalType === 'medio' && (
            <>
              <Form.Item label="Nombre" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: Radio, Digital..." />
              </Form.Item>
              <Form.Item label="Mix Anual (0.0 a 1.0)" name="mixAnual" rules={[{ required: true }]}>
                <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Peso Estratégico" name="pesoEstrategico" rules={[{ required: true }]}>
                <InputNumber min={0} max={5} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          {(modalType === 'presupuesto' || modalType === 'venta') && (
            <>
              <Form.Item label="Ejecutivo" name="ejecutivoId" rules={[{ required: true }]}>
                <Select disabled>
                  {ejecutivos.map(e => <Select.Option key={e.id} value={e.id}>{e.nombre}</Select.Option>)}
                </Select>
              </Form.Item>
              <Form.Item label="Medio" name="medioId" rules={[{ required: true }]}>
                <Select disabled>
                  {medios.map(m => <Select.Option key={m.id} value={m.id}>{m.nombre}</Select.Option>)}
                </Select>
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Año" name="anio" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Mes" name="mes" rules={[{ required: true }]}>
                    <InputNumber min={1} max={12} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              {modalType === 'presupuesto' ? (
                <>
                  <Form.Item label="Valor Presupuesto" name="presupuesto" rules={[{ required: true }]}>
                    <InputNumber
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label="Tipo" name="tipo">
                    <Input placeholder="Ej: Base, Ajuste" />
                  </Form.Item>
                </>
              ) : (
                <Form.Item label="Valor Venta Real" name="ventaReal" rules={[{ required: true }]}>
                  <InputNumber
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )}
            </>
          )}

          {modalType === 'medio' && (
            <>
              <Form.Item label="Nombre del Medio" name="nombre" rules={[{ required: true }]}>
                <Input placeholder="Ej: TV, Radio, Digital" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Mix Anual (0 a 1)" name="mixAnual" rules={[{ required: true }]}>
                    <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Peso Estratégico" name="pesoEstrategico" rules={[{ required: true }]}>
                    <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminDashboard;